import React, { useEffect } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import {
  Table,
  FormFeedback,
  ButtonIcon,
  ButtonText,
  TagInputProps,
  TagInput
} from '@morressier/alembic';
import _ from 'lodash';
import { ISubmissionMetadata } from '@publishing-engine/domain/models';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { FormFieldFunding } from '@publishing-engine/domain/types/article';
import { IGrantNumbers } from 'src/pages/Organization/public/Journal/SubmissionUpload/components/Funding';
import { FieldLabel } from 'src/components/Field';
import { RadioGroup } from 'src/components/RadioGroup';
import FunderSearch, {
  IFunder
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/FunderSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';

export type IFundingValue = {
  metadata: {
    funding: ISubmissionMetadata['funding'];
  };
};

export interface IFundingGrantNumbersProps {
  initialValues?: IGrantNumbers;
  onValueChange: (grantNumbers: IGrantNumbers) => void;
}
export type ExcludedTagInputProps = Omit<TagInputProps, 'name' | 'control'>;

const FundingGrantNumbers = ({
  onValueChange,
  initialValues,
  ...props
}: IFundingGrantNumbersProps & ExcludedTagInputProps) => {
  const { control, watch } = useForm<{ grantNumbers?: IGrantNumbers }>({
    defaultValues: { grantNumbers: initialValues }
  });

  const grantNumbers = watch('grantNumbers');

  useEffect(() => {
    if (grantNumbers !== undefined) onValueChange(grantNumbers);
  }, [grantNumbers]);

  return (
    <TagInput
      {...props}
      name="grantNumbers"
      control={control}
      helperText="Press Enter to add grant or award numbers"
      placeholder="Enter grant or award numbers"
    />
  );
};

interface Props {
  formField: FormFieldFunding;
}

const FundingForm: React.FC<Props> = ({ formField }) => {
  const {
    control,
    formState: { errors },
    watch,
    register,
    clearErrors,
    setValue
  } = useFormContext<IFundingValue>();
  const {
    params: { validations, labels },
    caseDataFields: { main: field, dependsOn }
  } = formField;

  const dependsOnField = `${field}.${dependsOn}` as const;
  const mainField = `${field}.value` as const;

  const error = _.get(errors, field);

  const funders = watch(mainField);
  const isFunded = watch(dependsOnField);
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: mainField
  });

  const addFundingRow = () => {
    clearErrors(mainField);
    append({ fundingBody: '', grantNumber: '' });
  };

  if (isFunded && !fields.length) {
    addFundingRow();
  }

  const handleGrantNumberChange = (
    grantNumbers: IGrantNumbers,
    index: number
  ) => {
    const grantNumber = grantNumbers?.map(({ text }) => text).join(',') || '';

    setValue(`${mainField}.${index}.grantNumber`, grantNumber, {
      shouldValidate: !!grantNumbers?.length
    });
  };

  useEffect(() => {
    // Clear the funding information when 'isFunded' is false
    if (!isFunded) {
      setValue(mainField, []); // Set the funders array to an empty array
      clearErrors(mainField);
    }
  }, [isFunded, setValue, clearErrors, mainField]);

  return (
    <Flex flexDirection="column" gap={8} width={'100%'}>
      <Flex flexDirection="column" gap={2}>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingSection">Update {labels.main}</Text>
        </Flex>
        <Flex flexDirection="column" gap={6} alignItems="flex-start">
          <FieldLabel
            isRequired={validations.required}
            label={labels.section.title}
            subText=""
          />

          <Flex flexDirection="column" gap={4}>
            <RadioGroup
              name={dependsOnField}
              control={control}
              options={[
                {
                  value: false,
                  children: 'No'
                },
                {
                  value: true,
                  children: 'Yes'
                }
              ]}
            />
          </Flex>

          {isFunded ? (
            <>
              <FieldLabel
                isRequired={validations.required}
                label={labels.main}
                subText={labels.subtitle}
              />
              {/* --- Funding rows --- */}
              <Table>
                <Table.HeaderRow>
                  <Table.HeaderCell flex="5" textAlign="left">
                    Funding Body
                  </Table.HeaderCell>
                  <Table.HeaderCell flex="5" textAlign="left">
                    Grant or Award Numbers
                  </Table.HeaderCell>
                  <Table.HeaderCell flex="1" />
                </Table.HeaderRow>
                <Table.Body sx={{ borderTop: 'none' }}>
                  {fields.map((field, index) => (
                    <Table.Row key={field.id} gap={4} alignItems="baseline">
                      <Table.RowCell
                        flex="5"
                        textStyle="headingItem"
                        textAlign="left"
                      >
                        <Flex flexDirection="column" gap={2}>
                          <FunderSearch
                            {...register(`${mainField}.${index}.fundingBody`)}
                            isInvalid={!!error?.value?.[index]?.fundingBody}
                            placeholder="Search for funders"
                            onFunderSelect={(funder: IFunder) => {
                              update(index, {
                                ...field,
                                fundingBody: funder.name
                              });
                            }}
                            onAddUnverifiedFunder={() => {
                              clearErrors(`${mainField}.${index}.fundingBody`);
                            }}
                          />
                          {error?.value?.[index]?.fundingBody && (
                            <FormFeedback variant="error">
                              Funding Body is required
                            </FormFeedback>
                          )}
                        </Flex>
                      </Table.RowCell>
                      <Table.RowCell
                        flex="5"
                        textStyle="headingItem"
                        textAlign="left"
                      >
                        <FundingGrantNumbers
                          initialValues={
                            funders?.[index]?.grantNumber
                              ? funders[index].grantNumber
                                  .split(',')
                                  .map(v => ({ text: v }))
                              : undefined
                          }
                          onValueChange={grantNumbers =>
                            handleGrantNumberChange(grantNumbers, index)
                          }
                          errorMessage={
                            error?.value?.[index]?.grantNumber
                              ? 'Grant or Award Number is required'
                              : null
                          }
                        />
                      </Table.RowCell>
                      <Table.RowCell flex="1">
                        <ButtonIcon
                          icon={<FontAwesomeIcon icon={faTrash} />}
                          ariaLabel="Delete Funder"
                          onClick={() => remove(index)}
                        />
                      </Table.RowCell>
                    </Table.Row>
                  ))}
                </Table.Body>
                <ButtonText
                  iconLeft={<FontAwesomeIcon icon={faPlus} />}
                  onClick={addFundingRow}
                >
                  Add {fields.length > 0 ? 'another' : ''} fund
                </ButtonText>
              </Table>

              <Flex alignItems="center" width="100%">
                {error?.exists ? (
                  <FormFeedback variant="error">
                    {error?.exists.message}
                  </FormFeedback>
                ) : null}
              </Flex>
            </>
          ) : null}

          {error?.exists ? (
            <FormFeedback variant="error">
              Please indicate if there is funding to report for this submission.
            </FormFeedback>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  );

  // return (
  //   <Modal
  //     isOpen={isOpen}
  //     onClose={onClose}
  //     width="755px"
  //     closeButtonProps={{
  //       hidden: true
  //     }}
  //   >
  //     <FormProvider {...methods}>
  //       <form onSubmit={handleSubmit(onSave)}>
  //         <Flex flexDirection="column" gap={8} alignItems="flex-start">
  //           <OutlineCard borderless padding={8} width="100%">
  //             <Flex flexDirection="column" gap={2}>
  //               <Text textStyle="headingSection">{labels.section.title}</Text>
  //             </Flex>
  //             <Flex flexDirection="column" gap={2}>
  //               <Flex flexDirection="column" gap={6} alignItems="flex-start">
  //                 <FieldLabel
  //                   isRequired={validations.required}
  //                   label={labels.main}
  //                   subText={labels.subtitle}
  //                 />

  //                 <Flex flexDirection="column" gap={4}>
  //                   <RadioGroup
  //                     name={dependsOnField}
  //                     control={control}
  //                     options={[
  //                       {
  //                         value: false,
  //                         children: 'No'
  //                       },
  //                       {
  //                         value: true,
  //                         children: 'Yes'
  //                       }
  //                     ]}
  //                   />
  //                 </Flex>

  //                 {isFunded ? (
  //                   <>
  //                     {/* --- Funding rows --- */}
  //                     <Table>
  //                       <Table.HeaderRow>
  //                         <Table.HeaderCell flex="5" textAlign="left">
  //                           Funding Body
  //                         </Table.HeaderCell>
  //                         <Table.HeaderCell flex="5" textAlign="left">
  //                           Grant or Award Numbers
  //                         </Table.HeaderCell>
  //                         <Table.HeaderCell flex="1" />
  //                       </Table.HeaderRow>
  //                       <Table.Body sx={{ borderTop: 'none' }}>
  //                         {fields.map((field, index) => (
  //                           <Table.Row
  //                             key={field.id}
  //                             gap={4}
  //                             alignItems="baseline"
  //                           >
  //                             <Table.RowCell
  //                               flex="5"
  //                               textStyle="headingItem"
  //                               textAlign="left"
  //                             >
  //                               <Flex flexDirection="column" gap={2}>
  //                                 <FunderSearch
  //                                   {...register(
  //                                     `${mainField}.${index}.fundingBody`
  //                                   )}
  //                                   isInvalid={
  //                                     !!error?.value?.[index]?.fundingBody
  //                                   }
  //                                   placeholder="Search for funders"
  //                                   onFunderSelect={(funder: IFunder) => {
  //                                     update(index, {
  //                                       ...field,
  //                                       fundingBody: funder.name
  //                                     });
  //                                   }}
  //                                   onAddUnverifiedFunder={() => {
  //                                     clearErrors(
  //                                       `${mainField}.${index}.fundingBody`
  //                                     );
  //                                   }}
  //                                 />
  //                                 {error?.value?.[index]?.fundingBody && (
  //                                   <FormFeedback variant="error">
  //                                     Funding Body is required
  //                                   </FormFeedback>
  //                                 )}
  //                               </Flex>
  //                             </Table.RowCell>
  //                             <Table.RowCell
  //                               flex="5"
  //                               textStyle="headingItem"
  //                               textAlign="left"
  //                             >
  //                               <FundingGrantNumbers
  //                                 initialValues={
  //                                   funders?.[index]?.grantNumber
  //                                     ? funders[index].grantNumber
  //                                         .split(',')
  //                                         .map(v => ({ text: v }))
  //                                     : undefined
  //                                 }
  //                                 onValueChange={grantNumbers =>
  //                                   handleGrantNumberChange(grantNumbers, index)
  //                                 }
  //                                 errorMessage={
  //                                   error?.value?.[index]?.grantNumber
  //                                     ? 'Grant or Award Number is required'
  //                                     : null
  //                                 }
  //                               />
  //                             </Table.RowCell>
  //                             <Table.RowCell flex="1">
  //                               <ButtonIcon
  //                                 icon={<FontAwesomeIcon icon={faTrash} />}
  //                                 ariaLabel="Delete Funder"
  //                                 onClick={() => remove(index)}
  //                               />
  //                             </Table.RowCell>
  //                           </Table.Row>
  //                         ))}
  //                       </Table.Body>
  //                       <ButtonText
  //                         iconLeft={<FontAwesomeIcon icon={faPlus} />}
  //                         onClick={addFundingRow}
  //                       >
  //                         Add {fields.length > 0 ? 'another' : ''} fund
  //                       </ButtonText>
  //                     </Table>

  //                     <Flex alignItems="center" width="100%">
  //                       {error?.exists ? (
  //                         <FormFeedback variant="error">
  //                           {error?.exists.message}
  //                         </FormFeedback>
  //                       ) : null}
  //                     </Flex>
  //                   </>
  //                 ) : null}

  //                 {error?.exists ? (
  //                   <FormFeedback variant="error">
  //                     Please indicate if there is funding to report for this
  //                     submission.
  //                   </FormFeedback>
  //                 ) : null}
  //               </Flex>
  //               <Flex gap={2} justifyContent="flex-end">
  //                 <Button
  //                   variant="secondary"
  //                   onClick={onClose}
  //                   borderRadius="small"
  //                 >
  //                   Exit, without Saving
  //                 </Button>
  //                 <Button variant="primary" type="submit" borderRadius="small">
  //                   Save Changes
  //                 </Button>
  //               </Flex>
  //             </Flex>
  //           </OutlineCard>
  //         </Flex>
  //       </form>
  //     </FormProvider>
  //   </Modal>
  // );
};

export default FundingForm;
